import type { TFunction } from 'i18next';

import {
  PetProfileAvatarModelImageSourceEnum,
  PetProfileModel,
} from '@/generated-client/pet-profile-a';

import { generateRandomNumberWithinRange } from './uuid';

export const petHeroImages: Record<PetType, string> = {
  Dog: 'https://image.chewy.com/catalog/general/images/dog-hero-image-v2._SY500_.jpg',
  Cat: 'https://image.chewy.com/catalog/general/images/cat-hero-image-v2._SY650_.jpg',
  General: 'https://image.chewy.com/catalog/general/images/general-hero-image._SY650_.jpg',
};

export type PetType = 'Dog' | 'Cat' | 'General';

export const PetType = {
  DOG: 'Dog',
  CAT: 'Cat',
  GENERAL: 'General',
} as const;

export const getPetType = (petProfiles: PetProfileModel[]): PetType => {
  const hasDogs = petProfiles.some(profile => profile?.type?.name === PetType.DOG);
  const hasCats = petProfiles.some(profile => profile?.type?.name === PetType.CAT);

  if (hasDogs && !hasCats) return PetType.DOG;
  if (!hasDogs && hasCats) return PetType.CAT;

  return PetType.GENERAL;
};

export const getPetNames = (pets: PetProfileModel[]): string => {
  switch (pets.length) {
    case 1:
      return pets[0].name;
    case 2:
      return `${pets[0].name} and ${pets[1].name}`;
    default:
      return 'your pets';
  }
};

export const getRewardsPetNames = (
  petProfiles: PetProfileModel[],
  t: TFunction,
): string | undefined => {
  const petNames = petProfiles
    .map(profile => profile.name?.trim())
    .filter((name): name is string => Boolean(name))
    .map(name => `${name.charAt(0).toUpperCase()}${name.slice(1)}`);

  if (petNames.length === 1) return petNames[0];
  if (petNames.length === 2) {
    return t('loyalty.membership.dashboard.v4.available-rewards.pet-names-two', {
      firstPetName: petNames[0],
      secondPetName: petNames[1],
    });
  }
  if (petNames.length >= 3) {
    return t('loyalty.membership.dashboard.v4.available-rewards.pet-names-pack', {
      firstPetName: petNames[0],
      secondPetName: petNames[1],
    });
  }

  return undefined;
};

export const getPetProfilePicture = (pets: PetProfileModel[]) => {
  const petPictures: string[] = pets
    .filter(
      pet =>
        pet.avatar?.imageSource === PetProfileAvatarModelImageSourceEnum.Photo &&
        pet.avatar?.imageUri,
    )
    .map(pet => pet.avatar?.imageUri)
    .filter((imageUri): imageUri is string => typeof imageUri === 'string');

  if (petPictures.length === 0) {
    return null;
  }

  const petIndex = generateRandomNumberWithinRange(petPictures.length);
  return removeDynamicSubstring(petPictures[petIndex]);
};

export function removeDynamicSubstring(url: string): string {
  const regex = /\._SS\d+_/;
  return url.replace(regex, '');
}

export const generatePetNamesString = (petProfiles: PetProfileModel[]): string => {
  if (!petProfiles?.length) return 'you';

  if (petProfiles.length === 1) {
    return `you and ${petProfiles[0].name}`;
  }

  if (petProfiles.length === 2) {
    return `you, ${petProfiles[0].name} and ${petProfiles[1].name}`;
  }

  const allButLast = petProfiles
    .slice(0, -1)
    .map(p => p.name)
    .join(', ');
  const lastName = petProfiles[petProfiles.length - 1].name;
  return `you, ${allButLast} and ${lastName}`;
};
